import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const SUPPLIER_CATEGORIES = [
  { value: "garage", label: "Garage" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "pneumatiques", label: "Pneumatiques" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "assurance", label: "Assurance" },
  { value: "depannage", label: "Dépannage" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "carburant", label: "Carburant" },
  { value: "location", label: "Location" },
  { value: "accessoires", label: "Accessoires" },
  { value: "autre", label: "Autre" },
];
export const SUPPLIER_CATEGORY_LABELS = Object.fromEntries(SUPPLIER_CATEGORIES.map((c) => [c.value, c.label]));

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";

// Sélecteur hybride : fournisseur de l'annuaire OU saisie libre + création rapide inline.
export default function SupplierPicker({ supplierId, provider, onChange, testid = "supplier-picker" }) {
  const [suppliers, setSuppliers] = useState([]);
  const [mode, setMode] = useState(supplierId ? "list" : "free");
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState("garage");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/vendor/suppliers").then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {});
  }, []);

  const onSelect = (v) => {
    if (v === "__free__") { setMode("free"); onChange({ supplier_id: null, provider: provider || "" }); return; }
    if (v === "__new__") { setMode("new"); return; }
    setMode("list");
    const s = suppliers.find((x) => x.id === v);
    onChange({ supplier_id: v || null, provider: s ? (s.trade_name || s.name) : "" });
  };

  const createQuick = async () => {
    if (!newName.trim()) { toast.error("Nom du fournisseur requis"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/vendor/suppliers", { name: newName.trim(), category: newCat });
      toast.success(`Fournisseur ${data.supplier.reference} créé`);
      setSuppliers((p) => [...p, data.supplier].sort((a, b) => a.name.localeCompare(b.name)));
      onChange({ supplier_id: data.supplier.id, provider: data.supplier.name });
      setMode("list");
      setNewName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setCreating(false);
  };

  return (
    <div className="space-y-2">
      <select
        value={mode === "new" ? "__new__" : (mode === "free" ? "__free__" : (supplierId || "__free__"))}
        onChange={(e) => onSelect(e.target.value)}
        className={inputCls}
        data-testid={testid}
      >
        <option value="__free__">— Saisie libre —</option>
        {suppliers.filter((s) => s.active !== false).map((s) => (
          <option key={s.id} value={s.id}>{s.trade_name || s.name} ({SUPPLIER_CATEGORY_LABELS[s.category] || s.category})</option>
        ))}
        <option value="__new__">+ Créer un fournisseur…</option>
      </select>
      {mode === "free" && (
        <input
          value={provider || ""}
          onChange={(e) => onChange({ supplier_id: null, provider: e.target.value })}
          placeholder="Nom du garage / prestataire"
          className={inputCls}
          data-testid={`${testid}-free-input`}
        />
      )}
      {mode === "new" && (
        <div className="flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Raison sociale *" className={inputCls} data-testid={`${testid}-new-name`} />
          <select value={newCat} onChange={(e) => setNewCat(e.target.value)} className={inputCls + " max-w-[140px]"} data-testid={`${testid}-new-category`}>
            {SUPPLIER_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button type="button" onClick={createQuick} disabled={creating} className="shrink-0 px-3 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`${testid}-new-submit`}>
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Créer"}
          </button>
        </div>
      )}
    </div>
  );
}
